import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import {
  onSingleMailApiCollection,
  // updateYouthAccount,
  createYouthAccountApi,
  findYouthAccountApi,
  updateYouthAccountApi,
} from '../../util/api';
import { parse } from '../../util/urlHelpers';
import { YOUTH_INVITE_STATUS } from '../../util/types';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/YouthAccountSettingsPage/CLEAR_UPDATED_FORM';

// Profile Update Actions
export const UPDATE_PROFILE_REQUEST = 'app/YouthAccountSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/YouthAccountSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/YouthAccountSettingsPage/UPDATE_PROFILE_ERROR';

// Youth Profile Actions
export const UPDATE_YOUTH_PROFILE_REQUEST =
  'app/YouthAccountSettingsPage/UPDATE_YOUTH_PROFILE_REQUEST';
export const UPDATE_YOUTH_PROFILE_SUCCESS =
  'app/YouthAccountSettingsPage/UPDATE_YOUTH_PROFILE_SUCCESS';
export const UPDATE_YOUTH_PROFILE_ERROR = 'app/YouthAccountSettingsPage/UPDATE_YOUTH_PROFILE_ERROR';

// Youth Account List Actions
export const FETCH_YOUTH_ACCOUNTS_REQUEST =
  'app/YouthAccountSettingsPage/FETCH_YOUTH_ACCOUNTS_REQUEST';
export const FETCH_YOUTH_ACCOUNTS_SUCCESS =
  'app/YouthAccountSettingsPage/FETCH_YOUTH_ACCOUNTS_SUCCESS';
export const FETCH_YOUTH_ACCOUNTS_ERROR = 'app/YouthAccountSettingsPage/FETCH_YOUTH_ACCOUNTS_ERROR';

// Create Youth Account Actions
export const CREATE_YOUTH_ACCOUNT_REQUEST =
  'app/YouthAccountSettingsPage/CREATE_YOUTH_ACCOUNT_REQUEST';
export const CREATE_YOUTH_ACCOUNT_SUCCESS =
  'app/YouthAccountSettingsPage/CREATE_YOUTH_ACCOUNT_SUCCESS';
export const CREATE_YOUTH_ACCOUNT_ERROR = 'app/YouthAccountSettingsPage/CREATE_YOUTH_ACCOUNT_ERROR';

// Update Youth Account Actions
export const UPDATE_YOUTH_ACCOUNT_REQUEST =
  'app/YouthAccountSettingsPage/UPDATE_YOUTH_ACCOUNT_REQUEST';
export const UPDATE_YOUTH_ACCOUNT_SUCCESS =
  'app/YouthAccountSettingsPage/UPDATE_YOUTH_ACCOUNT_SUCCESS';
export const UPDATE_YOUTH_ACCOUNT_ERROR = 'app/YouthAccountSettingsPage/UPDATE_YOUTH_ACCOUNT_ERROR';

// ================ Initial State ================ //

const initialState = {
  // Profile Update States
  updateInProgress: false,
  updateProfileError: null,

  // Youth Profile States
  updateYouthInProgress: null,
  updateYouthProfileError: null,
  updateYouthError: null,

  // Youth Account List States
  youthAccounts: [],
  pagination: null,
  fetchInProgress: false,
  fetchError: null,

  // Create States
  createInProgress: false,
  createError: null,

  // Update States
  updateAccountInProgress: false,
  updateAccountError: null,
};

// ================ Reducer ================ //

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    // Clear Form
    case CLEAR_UPDATED_FORM:
      return {
        ...state,
        updateProfileError: null,
        updateYouthProfileError: null,
        createError: null,
        updateAccountError: null,
      };

    // Profile Update
    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        updateInProgress: false,
        updateProfileError: payload,
      };

    // Youth Profile Update
    case UPDATE_YOUTH_PROFILE_REQUEST:
      return {
        ...state,
        updateYouthInProgress: payload,
        updateYouthProfileError: null,
      };
    case UPDATE_YOUTH_PROFILE_SUCCESS:
      return {
        ...state,
        updateYouthInProgress: null,
      };
    case UPDATE_YOUTH_PROFILE_ERROR:
      return {
        ...state,
        updateYouthInProgress: null,
        updateYouthProfileError: payload.error,
        updateYouthError: payload.email,
      };

    // Fetch Youth Accounts
    case FETCH_YOUTH_ACCOUNTS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_YOUTH_ACCOUNTS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        youthAccounts: payload.data,
        pagination: payload.pagination,
      };
    case FETCH_YOUTH_ACCOUNTS_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };

    // Create Youth Account
    case CREATE_YOUTH_ACCOUNT_REQUEST:
      return {
        ...state,
        createInProgress: true,
        createError: null,
      };
    case CREATE_YOUTH_ACCOUNT_SUCCESS:
      return {
        ...state,
        createInProgress: false,
        youthAccounts: [payload, ...state.youthAccounts],
      };
    case CREATE_YOUTH_ACCOUNT_ERROR:
      return {
        ...state,
        createInProgress: false,
        createError: payload,
      };

    // Update Youth Account
    case UPDATE_YOUTH_ACCOUNT_REQUEST:
      return {
        ...state,
        updateAccountInProgress: payload,
        updateAccountError: null,
      };
    case UPDATE_YOUTH_ACCOUNT_SUCCESS:
      return {
        ...state,
        updateAccountInProgress: false,
        youthAccounts: state.youthAccounts.map(account =>
          account._id === payload._id ? payload : account
        ),
        isEditModalOpen: false,
        selectedYouthAccount: null,
      };
    case UPDATE_YOUTH_ACCOUNT_ERROR:
      return {
        ...state,
        updateAccountInProgress: false,
        updateAccountError: payload,
      };

    default:
      return state;
  }
}

// ================ Action Creators ================ //

// Clear Form
export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// Profile Update
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});

export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});

export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// Youth Profile
export const updateYouthProfileRequest = params => ({
  type: UPDATE_YOUTH_PROFILE_REQUEST,
  payload: params,
});

export const updateYouthProfileSuccess = () => ({
  type: UPDATE_YOUTH_PROFILE_SUCCESS,
});

export const updateYouthProfileError = (error, email) => ({
  type: UPDATE_YOUTH_PROFILE_ERROR,
  payload: { error, email },
  error: true,
});

// Fetch Youth Accounts
export const fetchYouthAccountsRequest = () => ({
  type: FETCH_YOUTH_ACCOUNTS_REQUEST,
});

export const fetchYouthAccountsSuccess = response => ({
  type: FETCH_YOUTH_ACCOUNTS_SUCCESS,
  payload: response,
});

export const fetchYouthAccountsError = error => ({
  type: FETCH_YOUTH_ACCOUNTS_ERROR,
  payload: error,
});

// Create Youth Account
export const createYouthAccountRequest = type => ({
  type: CREATE_YOUTH_ACCOUNT_REQUEST,
  payload: type,
});

export const createYouthAccountSuccess = account => ({
  type: CREATE_YOUTH_ACCOUNT_SUCCESS,
  payload: account,
});

export const createYouthAccountError = error => ({
  type: CREATE_YOUTH_ACCOUNT_ERROR,
  payload: error,
});

// Update Youth Account
export const updateYouthAccountRequest = data => ({
  type: UPDATE_YOUTH_ACCOUNT_REQUEST,
  payload: data,
});

export const updateYouthAccountSuccess = account => ({
  type: UPDATE_YOUTH_ACCOUNT_SUCCESS,
  payload: account,
});

export const updateYouthAccountError = error => ({
  type: UPDATE_YOUTH_ACCOUNT_ERROR,
  payload: error,
});

// ================ Thunks ================ //

// Fetch Youth Accounts
export const fetchYouthAccounts = params => (dispatch, getState, sdk) => {
  dispatch(fetchYouthAccountsRequest());
  return findYouthAccountApi(params)
    .then(response => {
      dispatch(fetchYouthAccountsSuccess(response));
      return response;
    })
    .catch(error => {
      dispatch(fetchYouthAccountsError(storableError(error)));
      throw error;
    });
};

// Create Youth Account
export const createYouthAccount = (params, againFetchData) => (dispatch, getState, sdk) => {
  dispatch(createYouthAccountRequest());
  return createYouthAccountApi(params)
    .then(response => {
      const account = response?.data || response;
      dispatch(createYouthAccountSuccess(account));
      return dispatch(fetchYouthAccounts(againFetchData)).then(() => response);
    })
    .catch(error => {
      console.log(error, '&&& &&& => error');
      console.log(storableError(error), '&&& &&& => storableError(error)');
      dispatch(createYouthAccountError(storableError(error)));
      throw error;
    });
};

// Update Youth Account
export const updateYouthAccountDetails = (filter, payload, againFetchData) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(updateYouthAccountRequest(filter.invite_id));

  return updateYouthAccountApi({ filter, payload })
    .then(response => {
      console.log(response, '&&& &&& => response');
      const account = Array.isArray(response?.data) ? response.data[0] : response?.data || response;
      dispatch(updateYouthAccountSuccess(account));
      return dispatch(fetchYouthAccounts(againFetchData)).then(() => response);
    })
    .catch(error => {
      dispatch(updateYouthAccountError(storableError(error)));
      throw error;
    });
};

// Update Youth Account Status
export const updateYouthAccountStatus = (params, email) => (dispatch, getState, sdk) => {
  // dispatch(updateYouthProfileRequest(email));
  // return updateYouthAccount({ email })
  //   .then((res) => {
  //     if (res) {
  //       const queryParams = {
  //         expand: true,
  //         include: ['profileImage'],
  //         'fields.image': ['variants.square-small', 'variants.square-small2x'],
  //       };
  //       return sdk.currentUser.updateProfile(params, queryParams)
  //         .then((response) => {
  //           dispatch(updateYouthProfileSuccess(response));
  //           const entities = denormalisedResponseEntities(response);
  //           if (entities.length !== 1) {
  //             throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
  //           }
  //           const currentUser = entities[0];
  //           dispatch(currentUserShowSuccess(currentUser));
  //           dispatch(fetchYouthAccounts({}));
  //         });
  //     }
  //     return res;
  //   })
  //   .catch((error) => {
  //     dispatch(updateYouthProfileError(storableError(error), email));
  //     throw error;
  //   });
};

// Send Invite Email
export const inviteEmailToYouth = actionPayload => (dispatch, getState, sdk) => {
  const { youthsAccountDetails = [] } = actionPayload.publicData || {};
  const lastIndexData = youthsAccountDetails[youthsAccountDetails.length - 1] || {};

  if (lastIndexData?.accountActive) {
    const { uuid } = getState().user.currentUser.id || {};
    const data = {
      email: lastIndexData.youthEmail,
      name: 'Youth',
      signupUrl: `${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/signup/${lastIndexData.youthEmail}/${lastIndexData.youthDob}/${uuid}`,
    };
    return onSingleMailApiCollection({
      template_id: process.env.REACT_APP_INVITE_EMAIL_TO_YOUTH_TEMPLATE_ID,
      data,
    });
  }
  return null;
};

// Update Profile
export const updateProfile = actionPayload => (dispatch, getState, sdk) => {
  dispatch(updateProfileRequest());

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile(actionPayload, queryParams)
    .then(response => {
      dispatch(updateProfileSuccess(response));
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(inviteEmailToYouth(actionPayload));
    })
    .catch(e => dispatch(updateProfileError(storableError(e))));
};

const PER_PAGE_YOUTH_SHOW = 100;

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { page = 1 } = parse(search) || {};
  const fetchCurrentUserOptions = {
    updateNotifications: false,
  };

  // No need to listing data when creating a new listing
  return Promise.all([dispatch(fetchCurrentUser(fetchCurrentUserOptions))])
    .then(response => {
      const currentUser = getState().user.currentUser;
      const {
        id: { uuid },
      } = currentUser;

      return dispatch(
        fetchYouthAccounts({
          pageParams: {
            page: page,
            perPage: PER_PAGE_YOUTH_SHOW,
          },
          payload: {
            guardian_id: uuid,
            youth_status: {
              $in: [
                YOUTH_INVITE_STATUS.INVITE,
                YOUTH_INVITE_STATUS.ACCEPT,
                YOUTH_INVITE_STATUS.SUSPEND,
              ],
            },
          },
        })
      );
    })
    .catch(e => {
      throw e;
    });
};
